import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { EventsState } from "../../../../../../store/types";
import { CallToAction, LanguagesAbbr, PageModuleType } from "../../../../../../types/working-model";
import Icon, { ICONS, COLORS } from "../../../../../general-ui/icon";
import Modal from "../../../../../general-ui/modal/modal";
import { blurEditor, loadWorkingEvent, updateHomepageHeader } from "../../../../../../store/actions/admin/create-event";
import '../../../../../live-event/marketing-page/header/header.scss';
import './header.scss';
import Controls, { InitialControlsDisplay } from "../../../session/editor/moduleControls/controls";
import { UploadImageModal } from "../../../../../general-ui/image-upload-modal/image-upload-modal";
import NavItem, { checkLinkedSectionIsOn, filterActiveModules } from './nav-item';
import { LanguageDropdown } from "../../../../../general-ui/language-dropdown/language-dropdown";
import { HeaderAllTemplates, TemplateClassNames } from "../../../../../../types/template-layouts";
import { useHistory, useParams } from "react-router";
import classNames from "classnames";
import { GetDefaultCallToAction } from "../../../../../../store/utils/create-event";
import CallToActionForm from "../../../../../general-ui/call-to-action/call-to-action";
import { Button } from "../../../../../general-ui/button/button";
import { getHeaderStylingOverrides } from "../../../../../live-event/utils";
import NewDropdown from "../../../../../general-ui/dropdown/new-dropdown";
import { EIcon } from "../../../../../general-ui/icon/icon";
import { getHomepageDefaultLanguage } from "../../../../../../utils/get-language";
import { addChecklistItem } from "../../../../../../store/actions/authentication";
import { ChecklistTitles } from "../../../../../../store/reducers/admin/assistant";
import { ALERT_TYPES, showAlert } from "../../../../../general-ui/alert/alert-service";
import { ParamsProps } from "../../../../../live-event/live-event";
import '../../../../../live-event/marketing-page/header/header.scss';
import './header.scss';
import EditSectionColorsModal, { EContentColorOverrideNames } from "../../../session/editor/moduleControls/edit-section-colors-modal";

interface IEditorHeaderProps {
	handleScroll: (p: PageModuleType) => void;
	template: TemplateClassNames;
	language: LanguagesAbbr;
	baseLanguage: LanguagesAbbr
}

export function switchingLanguageAlert(): void {
	showAlert({
		message: "Fetching translations",
		description: "Please wait a moment while we get new translations",
		duration: 4000,
		type: ALERT_TYPES.POSITIVE
	});
}

const EditorHeader = React.forwardRef<HTMLDivElement, IEditorHeaderProps>(({
	handleScroll,
	template,
	language,
	baseLanguage,
}, ref) => {
	const {
		CreateEventReducer: {
			workingEvent,
			editorSize
		},
		AuthReducer: {
			token,
			user
		}
	} = useSelector((event: EventsState) => event);
	const dispatch = useDispatch();
	const header = workingEvent?.homepage?.header;
	const [logo, setLogo] = useState<string | null | undefined>(header?.logo);
	const [openEditLogoModal, setOpenEditLogoModal] = useState(false);
	const [openEditNavItemColor, setOpenEditNavItemColor] = useState(false);
	const [callToActionOpen, setCallToActionOpen] = useState(false);
	const [workingCallToAction, setWorkingCallToAction] = useState<CallToAction>(header?.callToAction || GetDefaultCallToAction());
	const [activePageModules, setActivePageModules] = useState(filterActiveModules(workingEvent?.homepage?.modules));
	const [selectedLanguage, setSelectedLanguage] = useState(language ?? getHomepageDefaultLanguage());

	const { id } = useParams() as ParamsProps;
	const {
		backgroundColor,
		backgroundImg,
		mainTextOverrides
	} = getHeaderStylingOverrides(workingEvent?.homepage?.header.styling_overrides);

	const layout = header?.layout || HeaderAllTemplates.left;

	const { t } = useTranslation("homepage");

	const registration_on = workingEvent?.registration_on;

	const navItems = header?.navItems;

	// get default language from first session and set that to the url language
	const history = useHistory();
	const languages = Array.from(new Set(workingEvent?.homepage?.languages as LanguagesAbbr[]));
	// Homepage doesnt have a default language set, use first sessions default language
	const firstDefaultLanguage = workingEvent?.sessions[0].default_language;
	// if default language changes, update the page
	useEffect(() => history.replace(language), [firstDefaultLanguage, history, language]);

	useEffect(() => setSelectedLanguage(language), [language]);

	useEffect(() => {
		setActivePageModules(filterActiveModules(workingEvent?.homepage?.modules));
	}, [workingEvent?.homepage?.modules]);

	const handleLanguageChange = (langAbbr: LanguagesAbbr) => {
		switchingLanguageAlert();
		dispatch(blurEditor(true));
		dispatch(loadWorkingEvent(id, token!));
		setSelectedLanguage(langAbbr);
		history.replace(langAbbr);
	};

	const saveLogo = useCallback((image: { original: string, edited: string; }) => {
		// TODO: refactor to keep original image for future editing
		if (header) {
			dispatch(updateHomepageHeader({
				...header,
				logo: image.edited
			}));
			// ASSISTANT CHECKLIST ITEM: complete users checklist item for uploading logo in header
			if (user && !user.onboarded.includes(ChecklistTitles.uploadLogo)) {
				dispatch(addChecklistItem(token!, user.id, user.onboarded, ChecklistTitles.uploadLogo));
			}
		}
		setLogo(image.edited);
	}, [dispatch, header, token, user]);

	function editButton() {
		setCallToActionOpen(true);
	}

	function cancelEditButton() {
		setCallToActionOpen(false);
	}

	function setCallToActionButton() {
		if (!workingEvent?.homepage) { return; }

		if (header && workingCallToAction) {
			dispatch(
				updateHomepageHeader({
					...header,
					callToAction: workingCallToAction
				})
			);
		}
		setCallToActionOpen(false);
	}

	function setNavItemColorOverrides(colorKey: string) {
		if (!workingEvent?.homepage || !workingEvent?.homepage?.header) return;
		const workingStylingOverrides = { ...workingEvent?.homepage.header?.styling_overrides };

		// since we are not updating the background, color key is sent back with -color. If color key is only -color,
		// then we are restoring to the default color.
		const isRestoringDefault = colorKey === "-color";

		// when implementing the callToAction overrides, make sure to include the navItems in the default
		if (!workingStylingOverrides?.content) {
			workingStylingOverrides.content = {
				navItems: { color: isRestoringDefault ? "" : colorKey },
				callToAction: { backgroundColor: '', borderColor: '', text: { color: '' } }
			};
		} else {
			workingStylingOverrides.content.navItems = { color: isRestoringDefault ? "" : colorKey };
		}

		if (workingStylingOverrides && colorKey) {
			dispatch(
				updateHomepageHeader({
					...workingEvent.homepage.header,
					styling_overrides: workingStylingOverrides
				})
			);
		}
	}
	// wiring for optional call to action button
	// function toggleCallToAction() {
	// 	if (header) {
	// 		dispatch(updateHomepageHeader({
	// 			...header,
	// 			callToAction: { ...header.callToAction, content: { isOn: !header.callToAction.content?.isOn ?? true } }
	// 		}));
	// 	}
	// }
	const renderLinks = () => {
		return navItems?.map((navItem, i) => {
			if (checkLinkedSectionIsOn(navItem, activePageModules)) {
				// get the overrideColor to reuse for the hover class
				const overrideColor = mainTextOverrides?.split('-')[0];
				return (
					<NavItem
						key={navItem.button_name.base + i}
						navItem={navItem}
						handleScroll={handleScroll}
						isEditor={true}
						className={classNames("navigation-link", mainTextOverrides, { [`${overrideColor}-hover`]: mainTextOverrides })
						}
					/>);
			}
		});
	};

	const displayHeaderControls: InitialControlsDisplay = {
		editLayout: true,
		editColors: true,
	};

	const stickyHeader = header?.is_sticky;

	return header ? (
		<>
			<div
				ref={ref}
				className={classNames("editor-wrapper header marketing-page-header header-with-language header-fixed-mobile-only", `header-${layout}`, template, backgroundColor, { sticky: stickyHeader })}
			>
				{/* Controls obscure hamburger icon in mobile view, removing them only in this editor size view */}
				{editorSize !== "mobile" && (
					<Controls
						display={displayHeaderControls}
						module={header}
						baseLanguage={baseLanguage}
						language={language}
					/>
				)}

				<div className={classNames("header-container")} style={backgroundImg}>
					<div className="header-col-left">
						{logo ? (
							<img
								className="header-logo"
								alt="Logo"
								onClick={() => setOpenEditLogoModal(true)}
								src={logo}
							/>
						) : (
							<h3
								onClick={() => setOpenEditLogoModal(true)}
							>
								{t("Your Logo")}
							</h3>
						)}
						<button onClick={() => setOpenEditLogoModal(true)} className="round edit-button">
							<Icon name={ICONS.EDIT} color={COLORS.WHITE} size={12} />
						</button>
					</div>
					<nav className="header-navigation">
						{editorSize === "desktop" && (
							<div className="nav-item-container">
								{renderLinks()}
								<button onClick={() => setOpenEditNavItemColor(true)} className="round edit-button">
									<Icon name={ICONS.EDIT} color={COLORS.WHITE} size={12} />
								</button>
							</div>
						)}
						{
							languages.length > 1 &&
							<LanguageDropdown
								template={template}
								languages={languages}
								currentLanguageCode={selectedLanguage}
								onLanguageChange={handleLanguageChange}
							/>
						}
						{editorSize !== "desktop" && (
							<NewDropdown
								className="menu-tablet"
								popupClassName="menu-tablet-popup"
								showArrow={false}
								renderTitle={({ isOpen }) =>
									isOpen ? (
										<EIcon name="close" size="large" />
									) : (
										<EIcon name="menu" size="large" />
									)
								}
							>
								<div className="tablet-links">{renderLinks()}</div>
								{registration_on && (
									<Button
										classButton="primary header-call-to-action"
										template={template}
										onClick={() => null}
									>{workingCallToAction?.button_name?.[language] || workingCallToAction?.button_name?.base}</Button>
								)}
							</NewDropdown>
						)}

					</nav>

					<div className='header-col-right'>
						{
							(header?.callToAction) && editorSize === "desktop" &&
							<>
								<Button
									classButton="header-call-to-action primary"
									tagName="a"
									template={template}
								>
									{header.callToAction.button_name[language] || header.callToAction.button_name.base}
								</Button>
								<div className="call-to-action-options">
									<button onClick={editButton} className="round edit-button"><Icon name={ICONS.EDIT} size={12} color={COLORS.WHITE} /></button>
									{/* 
										Leaving this commented code if product decides to 
										add feature in a future release
									 */}
									{/* <button className="round edit-button" onClick={toggleCallToAction}><Icon name={ICONS.DELETE} size={14} color={COLORS.WHITE} /></button> */}
								</div>
							</>
						}
					</div>
				</div>
				<UploadImageModal
					originalImage={header?.logo ?? undefined}
					fileTitle={"Logo"}
					open={openEditLogoModal}
					close={() => setOpenEditLogoModal(false)}
					onSave={saveLogo}
				/>
			</div>
			<Modal
				cancellable={true}
				closeable={false}
				open={callToActionOpen}
				onRequestClose={cancelEditButton}
				title={"Edit button"}
				footer={(
					<>
						<button onClick={cancelEditButton} style={{ marginRight: 16 }}>Cancel</button>
						<button onClick={setCallToActionButton} className="primary">Done</button>
					</>
				)}
			>
				<div className="call-to-action-settings">
					<CallToActionForm
						workingCTA={workingCallToAction}
						onWorkingCTAChange={setWorkingCallToAction}
						language={language}
						baseLanguage={baseLanguage}
					/>
				</div>
			</Modal>
			<EditSectionColorsModal
				open={openEditNavItemColor}
				onClose={() => setOpenEditNavItemColor(false)}
				onFinish={setNavItemColorOverrides}
				title={EContentColorOverrideNames.navItems}
			/>

		</>
	) : null;
});

EditorHeader.displayName = 'EditorHeader';

export default EditorHeader;
